import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MsdeFloatingInputComponent } from '@megha-shared-design-elements';
import { SupabaseService } from '../../services/supabase.service';
import { StoreService } from '../../services/store.service';
import { MeghaPublicProfileService } from '../../services/megha-public-profile.service';
import {
  findPrimaryClinicStore,
  resolvePostLoginNavigation
} from '../../constants/clinic-home-route.util';
import { findPrimaryHostelStore } from '../../constants/hostel-home-route.util';
import { findPrimaryHotelStore } from '../../constants/hotel-home-route.util';

type AuthMode = 'signin' | 'register';
type LoginStep = 'identifier' | 'secret';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MsdeFloatingInputComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  @ViewChild('secretInput') secretInput?: MsdeFloatingInputComponent;

  email = '';
  password = '';
  confirmPassword = '';
  showPassword = false;
  mode: AuthMode = 'signin';
  step: LoginStep = 'identifier';
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  returnUrl = '/';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private supabaseService: SupabaseService,
    private storeService: StoreService,
    private meghaPublicProfileService: MeghaPublicProfileService
  ) {}

  get isRegisterMode(): boolean {
    return this.mode === 'register';
  }

  get canContinueIdentifier(): boolean {
    return this.isValidIdentifier(this.email);
  }

  get canSubmit(): boolean {
    if (this.step !== 'secret' || !this.password) {
      return false;
    }
    if (this.isRegisterMode) {
      return !!this.confirmPassword;
    }
    return true;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['rurl']) {
        this.returnUrl = params['rurl'];
      }
      if (params['mode'] === 'register') {
        this.mode = 'register';
      }
    });
  }

  onFormSubmit(): void {
    if (this.step === 'identifier') {
      this.onContinue();
      return;
    }
    this.onSubmit();
  }

  onContinue(): void {
    this.clearMessages();
    if (!this.canContinueIdentifier) {
      this.errorMessage = 'Enter a valid email or 10-digit mobile number.';
      return;
    }
    this.step = 'secret';
    setTimeout(() => this.secretInput?.nativeElement?.focus(), 50);
  }

  onSubmit(): void {
    if (this.isRegisterMode) {
      void this.onRegister();
      return;
    }
    void this.onLogin();
  }

  setRegisterMode(): void {
    this.mode = 'register';
    this.resetSecretStep();
  }

  setSignInMode(): void {
    this.mode = 'signin';
    this.resetSecretStep();
  }

  private resetSecretStep(): void {
    this.step = 'identifier';
    this.clearMessages();
    this.password = '';
    this.confirmPassword = '';
    this.showPassword = false;
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private isMobileNumber(input: string): boolean {
    const digitsOnly = input.replace(/\D/g, '');
    return digitsOnly.length >= 10 && digitsOnly.length <= 13 && /^\d+$/.test(digitsOnly);
  }

  private isValidIdentifier(input: string): boolean {
    const trimmed = input.trim();
    if (!trimmed) {
      return false;
    }
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return true;
    }
    return this.isMobileNumber(trimmed);
  }

  private normalizeEmailOrMobile(input: string): string {
    const trimmedInput = input.trim();

    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedInput)) {
      return trimmedInput;
    }

    if (this.isMobileNumber(trimmedInput)) {
      const digitsOnly = trimmedInput.replace(/\D/g, '');
      const mobileNumber = digitsOnly.slice(-10);
      return `${mobileNumber}@tekmegha.com`;
    }

    return trimmedInput;
  }

  async onLogin(): Promise<void> {
    if (!this.email || !this.password) {
      this.errorMessage = 'Please enter both email/mobile and password.';
      return;
    }

    this.isLoading = true;
    this.clearMessages();

    try {
      const normalizedEmail = this.normalizeEmailOrMobile(this.email);
      const { data, error } = await this.supabaseService.signInWithEmail(normalizedEmail, this.password);

      if (error) {
        this.errorMessage = error.message || 'Login failed. Please check your credentials and try again.';
        return;
      }

      if (data?.session) {
        this.successMessage = 'Login successful! Redirecting...';
        const target = await this.finishAuthenticatedSession(data.session.user?.id);
        this.navigateAfterAuth(target);
      } else {
        this.errorMessage = 'Login failed. No session created.';
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Login failed. Please try again.';
      this.errorMessage = message;
    } finally {
      this.isLoading = false;
    }
  }

  async onRegister(): Promise<void> {
    if (!this.email || !this.password || !this.confirmPassword) {
      this.errorMessage = 'Please fill in all fields.';
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.errorMessage = 'Passwords do not match.';
      return;
    }

    if (this.password.length < 6) {
      this.errorMessage = 'Password must be at least 6 characters long.';
      return;
    }

    const normalizedEmail = this.normalizeEmailOrMobile(this.email);
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!isValidEmail) {
      this.errorMessage = 'Please enter a valid email address or mobile number.';
      return;
    }

    this.isLoading = true;
    this.clearMessages();

    try {
      const { data, error } = await this.supabaseService.signUpWithEmail(normalizedEmail, this.password);

      if (error) {
        this.errorMessage = error.message || 'Registration failed. Please try again.';
        return;
      }

      if (data?.user && !data.session) {
        this.successMessage = 'Account created! Check your email to verify, then sign in.';
        this.setSignInMode();
        this.password = '';
      } else if (data?.session) {
        this.successMessage = 'Account created! Redirecting...';
        const target = await this.finishAuthenticatedSession(data.session.user?.id);
        this.navigateAfterAuth(target);
      } else {
        this.errorMessage = 'Registration failed. Please try again.';
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Registration failed. Please try again.';
      this.errorMessage = message;
    } finally {
      this.isLoading = false;
    }
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  onForgotPassword(): void {
    void this.router.navigate(['/reset-password']);
  }

  onBack(): void {
    if (this.step === 'secret') {
      this.step = 'identifier';
      this.password = '';
      this.confirmPassword = '';
      this.showPassword = false;
      this.clearMessages();
      return;
    }
    void this.router.navigate(['/']);
  }

  changeIdentifier(): void {
    this.step = 'identifier';
    this.password = '';
    this.confirmPassword = '';
    this.showPassword = false;
    this.clearMessages();
  }

  private async finishAuthenticatedSession(
    userId: string | undefined
  ): Promise<string | string[]> {
    let stores: Awaited<ReturnType<StoreService['getUserStores']>>['data'] = null;
    let profiles: Awaited<ReturnType<MeghaPublicProfileService['listProfilesByUserId']>> = [];

    try {
      const res = await this.storeService.getUserStores();
      stores = res.data;
      if (userId) {
        try {
          profiles = await this.meghaPublicProfileService.listProfilesByUserId(userId);
        } catch {
          profiles = [];
        }
      }
      const primary =
        findPrimaryHostelStore(stores) ||
        findPrimaryHotelStore(stores) ||
        findPrimaryClinicStore(stores) ||
        stores?.[0];
      if (primary?.store_type) {
        try {
          sessionStorage.setItem('tekmegha_store_type', String(primary.store_type));
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }

    return resolvePostLoginNavigation(stores, this.returnUrl, profiles);
  }

  private navigateAfterAuth(target: string | string[]): void {
    setTimeout(() => {
      if (Array.isArray(target)) {
        void this.router.navigate(target, { replaceUrl: true });
      } else {
        void this.router.navigateByUrl(target, { replaceUrl: true });
      }
    }, 600);
  }
}
